import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

// The reusable client half of usernameless passkey sign-in — the tricky bit,
// written once. Two entry points share one ceremony:
//   • conditional UI (autofill): armed on mount, silent, offers the passkey in
//     the browser/1Password autofill dropdown of an `autocomplete="… webauthn"`
//     input; resolves when the user picks it.
//   • the one-tap button: a modal `startAuthentication` on demand.
// @simplewebauthn/browser's WebAuthnAbortService is a singleton — starting a new
// ceremony auto-aborts the pending one, so the button (or the typed-email submit
// path) automatically supersedes the conditional call; we swallow the resulting
// AbortError. Cleanup cancels on unmount; an `ignore` flag guards StrictMode.

/** WebAuthn needs a secure context + platform support. */
const passkeySupported =
  typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

export interface UsePasskeyAutofillArgs<T> {
  /** Fetch discoverable auth options from the portal's discover-options route. */
  getOptions: () => Promise<PublicKeyCredentialRequestOptionsJSON>;
  /** Post the assertion to the portal's discover-verify route; resolve with the
   *  verified payload, or throw / resolve falsy on failure. */
  verify: (assertion: AuthenticationResponseJSON) => Promise<T>;
  /** Called with the verify payload on success (persist session + navigate). */
  onSuccess: (data: T) => void;
  /** Gate the conditional-UI mount effect (e.g. only on the login step, only
   *  when there's no existing session). Default true. */
  enabled?: boolean;
}

export interface UsePasskeyAutofill {
  /** Whether the platform supports WebAuthn at all (hide the button otherwise). */
  supported: boolean;
  /** True while the explicit button ceremony is running. */
  busy: boolean;
  /** Last button error (the conditional path never surfaces errors). */
  error: string | null;
  /** The one-tap button handler — modal passkey ceremony, no email needed. */
  signInWithPasskey: () => Promise<void>;
}

export function usePasskeyAutofill<T>({
  getOptions,
  verify,
  onSuccess,
  enabled = true,
}: UsePasskeyAutofillArgs<T>): UsePasskeyAutofill {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keep the latest callbacks without re-arming the conditional effect each render.
  const ref = useRef({ getOptions, verify, onSuccess });
  ref.current = { getOptions, verify, onSuccess };

  // Conditional UI (autofill) — armed on mount, silent.
  useEffect(() => {
    if (!enabled || !passkeySupported) return;
    let ignore = false;
    void (async () => {
      let autofillOk: boolean;
      try {
        autofillOk = await browserSupportsWebAuthnAutofill();
      } catch {
        autofillOk = false;
      }
      if (!autofillOk || ignore) return;

      let options: PublicKeyCredentialRequestOptionsJSON;
      try {
        options = await ref.current.getOptions();
      } catch {
        return; // discover-options failed → stay silent, OTP path still works
      }
      if (ignore) return;

      let assertion: AuthenticationResponseJSON;
      try {
        assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill: true });
      } catch {
        // AbortError (superseded by a modal ceremony / unmount) or NotAllowedError
        // (dismissed) — both benign; never surface an error from the silent path.
        return;
      }
      if (ignore) return;

      try {
        const data = await ref.current.verify(assertion);
        if (!ignore && data) ref.current.onSuccess(data);
      } catch {
        // Verify failed for the autofilled credential — stay silent; the user can
        // still type an email or use a code.
      }
    })();
    return () => {
      ignore = true;
      // Tear down the lingering conditional affordance on unmount.
      WebAuthnAbortService.cancelCeremony();
    };
    // Re-arm only if the gate flips; callbacks are read via ref.
  }, [enabled]);

  // One-tap button — modal ceremony, no email, no autofill.
  const signInWithPasskey = useCallback(async () => {
    if (!passkeySupported) return;
    setBusy(true);
    setError(null);
    let options: PublicKeyCredentialRequestOptionsJSON;
    try {
      options = await ref.current.getOptions();
    } catch {
      setError('Could not start passkey sign-in. Send yourself a login code instead.');
      setBusy(false);
      return;
    }

    let assertion: AuthenticationResponseJSON;
    try {
      assertion = await startAuthentication({ optionsJSON: options });
    } catch (err) {
      // Dismissed / aborted → not an error worth showing.
      const name = (err as Error | null)?.name;
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        setError('Passkey sign-in was interrupted. Try again, or send yourself a login code.');
      }
      setBusy(false);
      return;
    }

    try {
      const data = await ref.current.verify(assertion);
      if (data) {
        ref.current.onSuccess(data);
        return; // navigation happens in onSuccess; leave busy true through the transition
      }
      setError('Passkey sign-in failed. Send yourself a login code instead.');
    } catch {
      setError('Passkey sign-in failed. Send yourself a login code instead.');
    }
    setBusy(false);
  }, []);

  return { supported: passkeySupported, busy, error, signInWithPasskey };
}
